import { useState, useEffect } from 'react'
import { Toaster, toast } from 'sonner'
import { SupportLinks } from './components/SupportLinks'
import { ConsentExplainer } from './components/ConsentExplainer'
import { BoundaryCard } from './components/BoundaryCard'
import type { BoundaryCardData, CloseResponse } from './types'
import { 
  createLedger, 
  updateLedgerSection, 
  closeLedger, 
  getLedgerPrivate, 
  archiveCardPng, 
  getCheckin, 
  recordActuals,
  generateIcs 
} from './lib/api'

type View = 'landing' | 'editor' | 'success' | 'private' | 'checkin' | 'public-view'

interface EditorState {
  id?: string
  privateKey?: string
  project_name: string
  intention: string
  stated_values: Array<{ text: string; beneficiary?: string }>
  evidence_lines: Array<{ signal: string; support_level: 'none'|'anecdotal'|'partial'|'strong'; note?: string }>
  revealed: { prior_stated: string; actual_attention: string; actual_outcome?: string }
  discomfort: { description: string; loci: string[] }
  boundaries: Array<{ statement: string }>
  bridge: { action: string; not_doing: string; timebox_hours?: number }
  followup_consent: boolean
  pattern_consent: boolean
}

const emptyEditor: EditorState = {
  project_name: '',
  intention: '',
  stated_values: [{ text: '', beneficiary: '' }],
  evidence_lines: [{ signal: '', support_level: 'none' }],
  revealed: { prior_stated: '', actual_attention: '' },
  discomfort: { description: '', loci: [] },
  boundaries: [{ statement: '' }],
  bridge: { action: '', not_doing: '' },
  followup_consent: false,
  pattern_consent: false,
}

export default function App() {
  const [view, setView] = useState<View>('landing')
  const [editor, setEditor] = useState<EditorState>(emptyEditor)
  const [currentLedgerId, setCurrentLedgerId] = useState<string | null>(null)
  const [privateKey, setPrivateKey] = useState<string | null>(null)
  const [closedData, setClosedData] = useState<CloseResponse | null>(null)
  const [archivedPngUrl, setArchivedPngUrl] = useState<string | null>(null)
  const [isArchiving, setIsArchiving] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [checkinData, setCheckinData] = useState<any>(null)
  const [actualsText, setActualsText] = useState('')

  // Simple client-side "router" for the tiny app
  useEffect(() => {
    const path = window.location.pathname
    const params = new URLSearchParams(window.location.search)
    const hash = window.location.hash

    if (path.startsWith('/c/')) {
      const token = path.split('/c/')[1]
      loadCheckin(token)
    } else if (path.startsWith('/l/')) {
      const id = path.split('/l/')[1]
      const k = params.get('k') || (hash ? hash.slice(1) : null)
      if (id && k) {
        setCurrentLedgerId(id)
        setPrivateKey(k)
        loadPrivateLedger(id, k)
      }
    } else if (path.startsWith('/v/')) {
      // public view – for now just show a note + ability to open new
      setView('public-view')
    }
  }, [])

  async function loadPrivateLedger(id: string, key: string) {
    try {
      const data = await getLedgerPrivate(id, key)
      // hydrate editor minimally for appends / re-close card
      setEditor({
        ...emptyEditor,
        id,
        privateKey: key,
        project_name: data.project_name || '',
        intention: data.intention || '',
      })
      setCurrentLedgerId(id)
      setPrivateKey(key)
      setView('private')
      toast.success('Private ledger loaded')
    } catch (e: any) {
      toast.error('Could not load with that private key. Check the URL.')
    }
  }

  async function loadCheckin(token: string) {
    try {
      const data = await getCheckin(token)
      setCheckinData(data)
      setView('checkin')
    } catch (e) {
      toast.error('Check-in not found or not yet ready.')
      setView('landing')
    }
  }

  function updateEditor(partial: Partial<EditorState>) {
    setEditor(prev => ({ ...prev, ...partial }))
  }

  // Deliberate section "weighed" — we persist partials to D1 so user can truly leave and return
  async function markSectionWeighed(section: string, value: any) {
    if (!currentLedgerId || !privateKey) return
    try {
      await updateLedgerSection(currentLedgerId, privateKey, section, value)
    } catch (e) {
      // non-fatal for now; the close will still capture current client state
    }
  }

  function addStatedValue() {
    updateEditor({ 
      stated_values: [...editor.stated_values, { text: '', beneficiary: '' }] 
    })
  }
  function addEvidence() {
    updateEditor({ 
      evidence_lines: [...editor.evidence_lines, { signal: '', support_level: 'none' }] 
    })
  }
  function addBoundary() {
    updateEditor({ boundaries: [...editor.boundaries, { statement: '' }] })
  }

  async function startNewLedger() {
    setEditor(emptyEditor)
    setCurrentLedgerId(null)
    setPrivateKey(null)
    setClosedData(null)
    setArchivedPngUrl(null)
    setView('editor')
    // create immediately so we have an id + secret for autosave
    try {
      const res = await createLedger({ project_name: 'Untitled ledger', intention: '' })
      setCurrentLedgerId(res.ledger_id)
      setPrivateKey(res.private_control_url.split('k=')[1] || null)
      setEditor(prev => ({ ...prev, id: res.ledger_id, privateKey: res.private_control_url.split('k=')[1] || undefined }))
      // update URL without reload
      window.history.replaceState({}, '', `/l/${res.ledger_id}?k=${res.private_control_url.split('k=')[1]}`)
      toast.info('Ledger opened. Your private key is in the URL — save it.')
    } catch (e: any) {
      toast.error('Failed to open ledger. ' + (e.message || ''))
    }
  }

  async function handleClose() {
    if (!currentLedgerId || !privateKey) {
      toast.error('Missing ledger or private key')
      return
    }
    if (!editor.bridge.action || !editor.bridge.not_doing) {
      toast.error('Bridge-to-action and "what I will decline" are required to close.')
      return
    }
    setIsClosing(true)
    try {
      const payload = {
        project_name: editor.project_name || 'Untitled',
        intention: editor.intention,
        stated_values: editor.stated_values.filter(v => v.text.trim()),
        evidence_lines: editor.evidence_lines.filter(e => e.signal.trim()),
        revealed: editor.revealed,
        discomfort: editor.discomfort,
        boundaries: editor.boundaries.filter(b => b.statement.trim()),
        bridge: editor.bridge,
        followup_consent: editor.followup_consent,
        pattern_consent: editor.pattern_consent,
      }
      const res: CloseResponse = await closeLedger(currentLedgerId, privateKey, payload)
      setClosedData(res)
      setView('success')
      // update URL to the success private view
      window.history.replaceState({}, '', `/l/${res.ledger_id}?k=${privateKey}`)
      toast.success('Ledger closed. The Boundary Clarity Card is ready.')
    } catch (e: any) {
      toast.error('Close failed: ' + (e.message || 'Please try again.'))
    } finally {
      setIsClosing(false)
    }
  }

  async function handleArchivePng() {
    if (!closedData || !currentLedgerId || !privateKey) return
    setIsArchiving(true)
    try {
      // Render the visible card area to canvas (real modern web app behavior)
      const cardEl = document.getElementById('boundary-card')
      if (!cardEl) throw new Error('Card element not found')
      
      // Use a high-quality approach: draw the SVG image inside the card to canvas
      const img = cardEl.querySelector('img') as HTMLImageElement
      if (!img) throw new Error('SVG image not found')
      
      const canvas = document.createElement('canvas')
      canvas.width = 1440
      canvas.height = 1840
      const ctx = canvas.getContext('2d', { alpha: true })!
      ctx.fillStyle = '#fdfcf8'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      await new Promise<void>((resolve) => {
        const tempImg = new Image()
        tempImg.onload = () => {
          ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height)
          resolve()
        }
        tempImg.src = img.src
      })
      
      const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/png', 0.96))
      const url = await archiveCardPng(currentLedgerId, privateKey, blob)
      setArchivedPngUrl(url)
      toast.success('PNG archived to R2')
    } catch (e: any) {
      toast.error('Archive failed: ' + (e.message || ''))
    } finally {
      setIsArchiving(false)
    }
  }

  function handlePrintCard() {
    window.print()
  }

  function handleDownloadSvg() {
    if (!closedData) return
    const svg = document.querySelector('#boundary-card img')?.getAttribute('src')
    if (!svg) return
    const link = document.createElement('a')
    link.href = svg
    link.download = `boundary-card-${closedData.ledger_id}.svg`
    link.click()
  }

  async function handleIcsDownload() {
    if (!currentLedgerId || !privateKey || !closedData) return
    try {
      const blob = await generateIcs(currentLedgerId, privateKey, 14)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `attention-ledger-reweigh-${currentLedgerId}.ics`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Calendar file downloaded. Add it to your calendar — no notification will be sent by us.')
    } catch (e) {
      toast.error('Could not generate calendar file')
    }
  }

  async function handleRecordActuals() {
    if (!checkinData?.token || !actualsText.trim()) return
    try {
      await recordActuals(checkinData.token, actualsText)
      toast.success('Actuals recorded. Thank you for the re-weighing.')
      // refresh
      const fresh = await getCheckin(checkinData.token)
      setCheckinData(fresh)
    } catch (e) {
      toast.error('Failed to record actuals')
    }
  }

  // === RENDER ===

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-[#f4f3ef] text-ink">
        <div className="max-w-3xl mx-auto px-5 pt-16 pb-24">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[11px] tracking-[3px] text-ink-light mb-1">PUBLIC UTILITY</div>
              <h1 className="text-6xl font-serif tracking-tighter text-boundary">attention<br />ledger</h1>
            </div>
            <SupportLinks />
          </div>

          <div className="mt-12 max-w-[52ch] text-[17px] leading-tight text-ink-muted">
            A single deliberate pass for solo creators building public tools.<br />
            Weigh what you state against what you can actually evidence before you book more attention.
          </div>

          <div className="mt-8 text-sm text-ink-light max-w-prose">
            The output is not motivation or a plan. It is a <span className="text-ink">Boundary Clarity Card</span> — 
            a restrained, printable artifact that records your stated values, the evidence you actually have, 
            the boundaries you set, and one bridge-to-action plus what you will decline until it is done.
          </div>

          <button 
            onClick={startNewLedger}
            className="primary mt-10 px-8 py-3.5 text-base rounded-md font-medium"
          >
            Open a new ledger
          </button>

          <div className="mt-6 text-xs text-ink-light">
            Or paste a private control link in the address bar (it contains ?k=your-secret).
          </div>

          <div className="mt-20 border-t border-[#d1cfc6] pt-8 text-[13px] text-ink-muted max-w-prose">
            No accounts. No streaks. No notifications. No dopamine. <br />
            The only loop is the one you deliberately open again in 14 days using a link you saved.
          </div>

          <div className="mt-16">
            <SupportLinks />
          </div>
        </div>
      </div>
    )
  }

  if (view === 'editor') {
    return (
      <div className="min-h-screen bg-[#f4f3ef] pb-32">
        <div className="max-w-3xl mx-auto px-5 pt-10">
          <div className="flex items-center justify-between mb-8">
            <div onClick={() => setView('landing')} className="cursor-pointer text-sm text-ink-light hover:text-ink">← back</div>
            <SupportLinks compact />
          </div>

          <div className="mb-8">
            <div className="text-[11px] tracking-[2px] text-ink-light">ATTENTION LEDGER</div>
            <div className="text-4xl font-serif tracking-tighter mt-1">Close the ledger</div>
            <div className="text-ink-muted mt-2 text-[15px]">Take your time. This is the only artifact most people will ever see from this process.</div>
          </div>

          {/* Project */}
          <div className="section">
            <h3 className="font-medium mb-3">1. Project</h3>
            <input 
              type="text" 
              placeholder="Project name (public handle or title)" 
              value={editor.project_name} 
              onChange={e => updateEditor({ project_name: e.target.value })}
              onBlur={() => markSectionWeighed('project', { project_name: editor.project_name })}
              className="w-full rounded px-4 py-3 text-lg"
            />
            <textarea 
              placeholder="One-sentence intention — what this is for, stated in public terms"
              value={editor.intention}
              onChange={e => updateEditor({ intention: e.target.value })}
              onBlur={() => markSectionWeighed('intention', { intention: editor.intention })}
              className="mt-3 w-full h-20 rounded px-4 py-3 resize-y"
            />
          </div>

          {/* Values Stated */}
          <div className="section">
            <h3 className="font-medium mb-3">2. Values stated</h3>
            <p className="text-sm text-ink-muted mb-3">What you explicitly claim this work will produce.</p>
            {editor.stated_values.map((v, i) => (
              <div key={i} className="mb-3 grid grid-cols-1 md:grid-cols-5 gap-3">
                <input 
                  className="md:col-span-3 rounded px-4 py-2.5" 
                  placeholder="I am stating that this will..." 
                  value={v.text} 
                  onChange={e => {
                    const copy = [...editor.stated_values]; copy[i] = { ...v, text: e.target.value }; updateEditor({ stated_values: copy })
                  }}
                />
                <input 
                  className="md:col-span-2 rounded px-4 py-2.5" 
                  placeholder="Who benefits and how" 
                  value={v.beneficiary || ''} 
                  onChange={e => { const copy = [...editor.stated_values]; copy[i] = { ...v, beneficiary: e.target.value }; updateEditor({ stated_values: copy }) }} 
                />
              </div>
            ))}
            <button onClick={addStatedValue} className="ghost text-xs px-3 py-1 rounded">+ another stated value</button>
          </div>

          {/* Evidence Lines */}
          <div className="section">
            <h3 className="font-medium mb-3">3. Evidence lines</h3>
            <p className="text-sm text-ink-muted mb-3">For the claims above — what concrete signal in 30–90 days would show it is true? What support do you have today?</p>
            {editor.evidence_lines.map((e, i) => (
              <div key={i} className="mb-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <input className="md:col-span-7 rounded px-4 py-2.5" placeholder="Observable signal that would confirm this claim" value={e.signal} onChange={ev => {
                  const copy = [...editor.evidence_lines]; copy[i] = { ...e, signal: ev.target.value }; updateEditor({ evidence_lines: copy })
                }} />
                <select className="md:col-span-3 rounded px-4 py-2.5 text-sm" value={e.support_level} onChange={ev => {
                  const copy = [...editor.evidence_lines]; copy[i] = { ...e, support_level: ev.target.value as any }; updateEditor({ evidence_lines: copy })
                }}>
                  <option value="none">no evidence yet</option>
                  <option value="anecdotal">anecdotal / hope</option>
                  <option value="partial">partial measurement</option>
                  <option value="strong">strong / already measured</option>
                </select>
                <input className="md:col-span-2 rounded px-4 py-2.5 text-sm" placeholder="note / link" value={e.note || ''} onChange={ev => {
                  const copy = [...editor.evidence_lines]; copy[i] = { ...e, note: ev.target.value }; updateEditor({ evidence_lines: copy })
                }} />
              </div>
            ))}
            <button onClick={addEvidence} className="ghost text-xs px-3 py-1 rounded">+ another evidence line</button>
          </div>

          {/* Values Revealed */}
          <div className="section">
            <h3 className="font-medium mb-3">4. Values revealed (one prior concrete example)</h3>
            <p className="text-sm text-ink-muted mb-3">Recall a previous public thing you shipped or planned at similar scale. What did you state then? What did your actual attention and outcome show?</p>
            <textarea className="w-full h-16 rounded px-4 py-3 mb-2" placeholder="What I stated at the time..." value={editor.revealed.prior_stated} onChange={e => updateEditor({ revealed: { ...editor.revealed, prior_stated: e.target.value } })} />
            <textarea className="w-full h-16 rounded px-4 py-3 mb-2" placeholder="What my actual allocation of attention and the outcome revealed..." value={editor.revealed.actual_attention} onChange={e => updateEditor({ revealed: { ...editor.revealed, actual_attention: e.target.value } })} />
          </div>

          {/* Discomfort */}
          <div className="section">
            <h3 className="font-medium mb-3">5. Discomfort</h3>
            <p className="text-sm text-ink-muted mb-3">Where does this currently feel thin, assumptive, or likely to slip? Be specific.</p>
            <textarea className="w-full h-24 rounded px-4 py-3" value={editor.discomfort.description} onChange={e => updateEditor({ discomfort: { ...editor.discomfort, description: e.target.value } })} placeholder="The part that makes my stomach tight is..." />
          </div>

          {/* Boundaries */}
          <div className="section">
            <h3 className="font-medium mb-3">6. Boundaries (hard constraints)</h3>
            {editor.boundaries.map((b, i) => (
              <input key={i} className="w-full mb-2 rounded px-4 py-2.5" placeholder="I will treat X as out of scope. Time cap: Y hours/week. Exit if Z not seen by date." value={b.statement} onChange={e => {
                const copy = [...editor.boundaries]; copy[i] = { statement: e.target.value }; updateEditor({ boundaries: copy })
              }} />
            ))}
            <button onClick={addBoundary} className="ghost text-xs px-3 py-1 rounded">+ another boundary</button>
          </div>

          {/* Bridge to action — the most important */}
          <div className="section">
            <h3 className="font-medium mb-3">7. Bridge-to-action choice (required to close)</h3>
            <p className="text-sm text-ink-muted mb-3">The smallest observable action you will take in the next 48–72 hours. And what you will explicitly decline until it is done.</p>
            <input className="w-full mb-3 rounded px-4 py-2.5" placeholder="The single smallest next action (e.g. 'write the first failing test for the core parser')" value={editor.bridge.action} onChange={e => updateEditor({ bridge: { ...editor.bridge, action: e.target.value } })} />
            <input className="w-full rounded px-4 py-2.5" placeholder="What I will decline / not expand until the above is complete" value={editor.bridge.not_doing} onChange={e => updateEditor({ bridge: { ...editor.bridge, not_doing: e.target.value } })} />
          </div>

          {/* Close + Consent (the visible model) */}
          <div className="mt-10 pt-8 border-t border-[#d1cfc6]">
            <div className="mb-5 text-sm font-medium tracking-wide text-ink">BEFORE CLOSING — THE CONSENT MODEL</div>
            <ConsentExplainer 
              followupChecked={editor.followup_consent}
              patternChecked={editor.pattern_consent}
              onFollowupChange={(v) => updateEditor({ followup_consent: v })}
              onPatternChange={(v) => updateEditor({ pattern_consent: v })}
            />

            <button 
              onClick={handleClose} 
              disabled={isClosing || !editor.bridge.action || !editor.bridge.not_doing}
              className="primary mt-8 w-full py-4 text-base rounded-md disabled:opacity-60"
            >
              {isClosing ? 'Closing the ledger…' : 'Close this ledger and generate the Boundary Clarity Card'}
            </button>
            <div className="text-center text-[11px] text-ink-light mt-3">This action is protected by Cloudflare Turnstile. Your private key is required to return later.</div>
          </div>
        </div>
      </div>
    )
  }

  if (view === 'success' && closedData) {
    const cardData: BoundaryCardData = closedData.card_preview || {
      project_name: editor.project_name,
      intention: editor.intention,
      stated_values: editor.stated_values.filter(v => v.text).map(v => ({ text: v.text, support: 'mixed' })),
      revealed_note: editor.revealed.actual_attention,
      discomfort_note: editor.discomfort.description,
      boundaries: editor.boundaries.filter(b => b.statement).map(b => b.statement),
      bridge_action: editor.bridge.action,
      not_doing: editor.bridge.not_doing,
      closed_at: new Date().toISOString().slice(0,10),
      ledger_short_id: closedData.ledger_id.slice(0,8),
    }

    return (
      <div className="min-h-screen bg-[#f4f3ef] py-10">
        <div className="max-w-3xl mx-auto px-5">
          <div className="text-center mb-6">
            <div className="text-xs tracking-[2px] text-ink-light">LEDGER CLOSED</div>
            <div className="text-3xl font-serif tracking-tight mt-1">Boundary Clarity Card</div>
          </div>

          <BoundaryCard 
            data={cardData} 
            onPrint={handlePrintCard}
            onArchivePng={handleArchivePng}
            onDownloadSvg={handleDownloadSvg}
            archivedUrl={archivedPngUrl || undefined}
            isArchiving={isArchiving}
          />

          {/* Private links + consent receipt — the heart of the visible model */}
          <div className="mt-8 max-w-2xl mx-auto space-y-6">
            <div className="bg-white border border-[#c9c6bb] rounded p-5 text-sm">
              <div className="font-medium mb-2">Your private control link (save this)</div>
              <div className="private-box p-3 text-xs break-all select-all">{closedData.private_control_url}</div>
              <div className="text-[11px] mt-2 text-ink-muted">Use this to revisit, append actuals, regenerate the card, or delete. Treat it like a password.</div>
              <button 
                onClick={() => { navigator.clipboard.writeText(closedData.private_control_url); toast.success('Private link copied') }}
                className="mt-3 text-xs underline"
              >copy private control link</button>
            </div>

            {closedData.followup_checkin_url && (
              <div className="bg-white border border-[#c9c6bb] rounded p-5 text-sm">
                <div className="font-medium mb-2">Your private 14-day check-in link (no notification will arrive)</div>
                <div className="private-box p-3 text-xs break-all select-all">{closedData.followup_checkin_url}</div>
                <div className="flex gap-3 mt-3">
                  <button onClick={() => { navigator.clipboard.writeText(closedData.followup_checkin_url!); toast.success('Check-in link copied. Save it now.') }} className="text-xs underline">copy check-in link</button>
                  <button onClick={handleIcsDownload} className="text-xs underline">download .ics calendar reminder</button>
                </div>
                <div className="mt-3 text-[11px] text-ink-muted">Add the .ics to your own calendar. We will never contact you. The page will only become useful if you visit it after the date.</div>
              </div>
            )}

            <div className="text-center">
              <button onClick={() => { window.location.href = '/' }} className="ghost px-5 py-2 text-sm rounded">Start another ledger</button>
            </div>

            <div className="pt-8 border-t text-center">
              <SupportLinks />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (view === 'private') {
    return (
      <div className="min-h-screen bg-[#f4f3ef] py-10">
        <div className="max-w-3xl mx-auto px-5">
          <div className="mb-6 flex items-center justify-between">
            <div className="text-sm">Private view — you hold the key</div>
            <SupportLinks compact />
          </div>

          <div className="text-2xl font-serif tracking-tight mb-4">Revisit or append actuals</div>
          <p className="text-ink-muted max-w-prose">This is your private control surface. You can generate a fresh card from current data or record what actually happened later.</p>

          <div className="mt-8 bg-white border border-[#c9c6bb] p-6 rounded">
            <div className="text-sm mb-2 text-ink-muted">Current project</div>
            <div className="text-xl font-serif">{editor.project_name || '—'}</div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => setView('editor')} className="primary px-5 py-2.5 text-sm rounded">Continue editing / re-close</button>
            <button onClick={() => window.print()} className="ghost px-5 py-2.5 text-sm rounded">Print current card (if closed)</button>
            <button onClick={async () => {
              if (!currentLedgerId || !privateKey) return
              await fetch(`/api/ledgers/${currentLedgerId}/delete`, { method: 'POST', headers: { 'x-owner-secret': privateKey } })
              toast.success('Ledger deleted')
              window.location.href = '/'
            }} className="text-xs px-4 py-2 text-warning hover:underline">Delete this ledger permanently</button>
          </div>

          <div className="mt-16">
            <SupportLinks />
          </div>
        </div>
      </div>
    )
  }

  if (view === 'checkin' && checkinData) {
    return (
      <div className="min-h-screen bg-[#f4f3ef] py-10">
        <div className="max-w-2xl mx-auto px-5">
          <div className="text-xs tracking-widest text-ink-light mb-1">DELAYED RE-WEIGHING</div>
          <h1 className="text-3xl font-serif tracking-tight">14-day reflection</h1>
          <div className="mt-3 text-sm text-ink-muted">Prepared by the system using only what you wrote when you closed the original ledger. No one else has seen it.</div>

          <div className="mt-8 bg-white border border-[#c9c6bb] p-6 rounded space-y-5">
            <div>
              <div className="label mb-1">Original project</div>
              <div className="font-serif text-xl">{checkinData.project_name}</div>
            </div>

            {checkinData.packet && (
              <div className="pt-4 border-t">
                <div className="label mb-2">Prepared reflection packet</div>
                <ul className="space-y-2 text-[15px]">
                  {checkinData.packet.questions?.map((q: string, i: number) => (
                    <li key={i} className="pl-1">• {q}</li>
                  ))}
                </ul>
                {checkinData.packet.suggested_reweigh && (
                  <div className="mt-4 text-sm bg-[#f8f7f4] p-3 border-l-2 border-[#3a4f41]">
                    {checkinData.packet.suggested_reweigh}
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="label mb-2">What actually happened since you closed the ledger?</div>
              <textarea 
                className="w-full h-28 rounded border border-[#d1cfc6] p-3 text-sm" 
                value={actualsText} 
                onChange={e => setActualsText(e.target.value)} 
                placeholder="Be specific. What signals appeared or failed to appear? How did your attention allocation compare to the boundaries you set?"
              />
              <button onClick={handleRecordActuals} disabled={!actualsText.trim()} className="primary mt-3 px-5 py-2 text-sm rounded disabled:opacity-50">Record actuals</button>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-ink-light">
            This page will remain available to you. No one else can reach it without the exact token.
          </div>

          <div className="mt-10 text-center">
            <SupportLinks />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f4f3ef] p-8 text-center text-sm text-ink-muted">
      Unknown view. <a href="/" className="underline">Return home</a>.
    </div>
  )
}
